import { DOCUMENT } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef, HostBinding,
    Inject,
    Input, OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';

import { BrowserOnly } from '@core/data-providers/platform/platform.decorators';
import { PlatformService } from '@core/services/platform/platform.service';
import { ContentItemTree } from '@features/cms-render/domain/models/contentItemTree';
import {
    CommonStylesRenderDirective,
} from '@features/cms-render/ui/directives/common-styles-render/common-styles-render.directive';

@Component({
    selector: 'piece-collapsible',
    templateUrl: './piece-collapsible.component.html',
    styleUrls: ['./piece-collapsible.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PieceCollapsibleComponent extends CommonStylesRenderDirective implements OnInit, AfterViewInit, OnDestroy {
    @Input() public element: ContentItemTree;

    @ViewChild('slotContent') public slotContent?: ElementRef;

    @HostBinding('attr.is-expanded')
    public isExpanded: boolean = false;

    private _isExpanded: boolean;

    public slotHeight: number = 0;

    public iconPadding: string = '0 0';

    public expandedBackgroundColor: string;

    public expandedTextColor: string;

    public iconColor: string;

    public expandedIconColor: string;

    private observer: MutationObserver;

    constructor(
        @Inject(DOCUMENT) private readonly document: Document,
        private readonly elRef: ElementRef,
        private readonly cdr: ChangeDetectorRef,
        private readonly platformService: PlatformService
    ) {
        super();
    }

    public toggleCollapsible(): void {
        this.isExpanded = !this.isExpanded;
        this._isExpanded = this.isExpanded;
        this.collapseAnimation().then();
    }

    @BrowserOnly<PieceCollapsibleComponent, undefined>(undefined)
    public async collapseAnimation() {
        const element: HTMLElement = await this.elRef.nativeElement;
        // Many collapsibles can exist, so I only want to close the ones that belong to my group.
        const collapsibles = element.closest('.single-active-collapse')?.querySelectorAll('piece-collapsible');

        const setStateAndProperties = () => {
            this.isExpanded = this._isExpanded;
            if (this.isExpanded) {
                this.open();
            } else {
                this.close();
            }
        };

        if (collapsibles?.length > 0) {
            collapsibles.forEach(el => {
                el.setAttribute('is-expanded', 'false');
            });
            setTimeout(setStateAndProperties, 200);
        } else {
            setStateAndProperties();
        }
    }

    public close() {
        this.slotHeight = 0;
        this.expandedBackgroundColor = undefined;
        this.expandedTextColor = undefined;
        this.cdr.detectChanges();
    }

    public open() {
        this.slotHeight = this.slotContent?.nativeElement.offsetHeight;
        this.expandedBackgroundColor = this.element.settings?.general?.expandedBackgroundColor;
        this.expandedTextColor = this.element.settings?.general?.expandedTextColor;
        this.cdr.detectChanges();
    }

    public ngOnInit(): void {
        this.iconColor = this.element.settings?.general?.iconColor;
        this.expandedIconColor = this.element.settings?.general?.expandedIconColor;
        this.isExpanded = this.element?.settings?.general?.isExpanded ?? false;
        this.expandedBackgroundColor = this.isExpanded ? this.element.settings?.general?.expandedBackgroundColor : undefined;
        this.expandedTextColor = this.isExpanded ? this.element.settings?.general?.expandedTextColor : undefined;
        this.iconPadding = this.element.settings?.general?.iconPosition === 'right' ? `0 ${this.element?.children[0]?.children[0]?.settings?.general?.textPaddingRight ?? 0}` : `${this.element?.children[0]?.children[0]?.settings?.general?.textPaddingLeft ?? 0} 0`;
        super.setDefaultHostStyles();
    }

    public async ngAfterViewInit() {
        if (await this.slotContent?.nativeElement) {
            this.slotHeight = this.isExpanded ? '0' : this.slotContent?.nativeElement.offsetHeight;
        }

        if (this.platformService.isBrowser()) {
            const el = this.elRef.nativeElement;
            this.observer = new MutationObserver(mutationsList => {
                for (const mutation of mutationsList) {
                    const _state = JSON.parse(el.getAttribute('is-expanded'));
                    if (mutation.type === 'attributes' && !_state && this.isExpanded !== _state) {
                        this.isExpanded = _state;
                        this.close();
                    }
                    if (mutation.type === 'attributes' && _state && this.isExpanded !== _state) {
                        this.isExpanded = _state;
                        this.open();
                    }
                }
            });
            const config: MutationObserverInit = { attributes: true };
            this.observer.observe(el, config);
        }
    }

    public ngOnDestroy() {
        if (!!this.observer) {
            this.observer.disconnect();
        }
    }
}
